import { db } from '../firebase';
import { collection, addDoc, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { sanitizeForFirestore } from './utils';

export type ChangeAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'RESTORE';

export interface ChangeLogEntry {
  id: string;
  who: string;            // display name of the acting user
  whoRole?: string;       // client | staff (admin/employee/contractor)
  action: ChangeAction;   // CREATE | UPDATE | DELETE | RESTORE
  entity: string;         // e.g. 'lead', 'job', 'contact', 'invoice', 'inventory', 'user'
  entityId: string;       // document id in Firestore
  entityLabel?: string;   // human label, e.g. customer/job name
  summary: string;        // one-line human-readable description
  changedFields?: string[]; // which top-level fields changed (UPDATE only)
  at: string;             // ISO timestamp
}

/**
 * Durable change ledger. Every create/edit/delete of a business record
 * (leads, jobs, contacts, invoices/quotes, inventory, users) writes an entry here.
 * Soft-deleted records are recorded with action DELETE but data stays in Firestore
 * (isDeleted flag), so history remains auditable.
 */
export async function logChange(params: {
  who: string;
  whoRole?: string;
  action: ChangeAction;
  entity: string;
  entityId: string;
  entityLabel?: string;
  summary: string;
  changedFields?: string[];
}): Promise<void> {
  try {
    const entry = {
      who: params.who || 'System',
      whoRole: params.whoRole || '',
      action: params.action,
      entity: params.entity,
      entityId: params.entityId,
      entityLabel: params.entityLabel || '',
      summary: params.summary,
      changedFields: params.changedFields || [],
      at: new Date().toISOString(),
    };
    await addDoc(collection(db, 'changeLog'), sanitizeForFirestore(entry));
  } catch (err) {
    // Never block a business operation because the ledger write failed —
    // but make the failure visible in the console for follow-up.
    console.error('[changeLog] Failed to write change ledger entry:', err);
  }
}

/** Read the most recent N change-ledger entries (for the admin Change Log view). */
export async function fetchRecentChanges(max = 200): Promise<ChangeLogEntry[]> {
  const q = query(collection(db, 'changeLog'), orderBy('at', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ChangeLogEntry));
}

/** CSV export helper — used by every record view so the team can verify saved data. */
export function exportCsv(filename: string, headers: string[], rows: (string | number | undefined | null)[][]) {
  const esc = (v: string | number | undefined | null) => {
    const s = v === undefined || v === null ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
